/**
 * Suggestion Show Component
 * Displays suggestion details with approve/reject actions
 */

import { useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  FunctionField,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Typography,
  Box,
  Button,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { semantic } from '@rekindle/shared/theme';

/**
 * Status color mapping
 */
const STATUS_COLORS: Record<string, string> = {
  pending: semantic.warning,
  approved: semantic.success,
  rejected: semantic.error,
  planned: semantic.info,
  done: semantic.neutral,
};

/**
 * Status field with chip
 */
function StatusChip() {
  const record = useRecordContext();
  if (!record?.status) return null;

  const status = record.status.toLowerCase();
  const color = STATUS_COLORS[status] || STATUS_COLORS.pending;

  return (
    <Chip
      label={record.status}
      sx={{
        backgroundColor: color,
        color: 'white',
        textTransform: 'capitalize',
      }}
    />
  );
}

/**
 * Action buttons for approving/rejecting suggestion
 */
function SuggestionActions() {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const handleStatusChange = async (status: string) => {
    setPendingStatus(status);
    setConfirmOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!record || !pendingStatus) return;

    try {
      await dataProvider.update(`content/suggestions/${record.id}/status`, {
        id: record.id,
        data: { status: pendingStatus },
        previousData: record,
      });
      notify(`Suggestion ${pendingStatus}`, { type: 'success' });
      refresh();
    } catch (error) {
      notify('Error updating suggestion status', { type: 'error' });
    }
    setConfirmOpen(false);
    setPendingStatus(null);
  };

  if (!record) return null;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
          onClick={() => handleStatusChange('approved')}
          disabled={record.status === 'approved'}
        >
          Approve
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<CloseIcon />}
          onClick={() => handleStatusChange('rejected')}
          disabled={record.status === 'rejected'}
        >
          Reject
        </Button>
        <Button
          variant="outlined"
          color="info"
          startIcon={<ScheduleIcon />}
          onClick={() => handleStatusChange('planned')}
          disabled={record.status === 'planned'}
        >
          Mark Planned
        </Button>
        <Button
          variant="outlined"
          startIcon={<DoneAllIcon />}
          onClick={() => handleStatusChange('done')}
          disabled={record.status === 'done'}
        >
          Mark Done
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark this suggestion as "{pendingStatus}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmStatusChange} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Title for the show page
 */
function SuggestionTitle() {
  const record = useRecordContext();
  return <span>Suggestion {record ? `"${record.title?.substring(0, 30)}..."` : ''}</span>;
}

export function SuggestionShow() {
  return (
    <Show title={<SuggestionTitle />}>
      <SimpleShowLayout>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Suggestion Details</Typography>
            <StatusChip />
          </Box>

          {/* User Info */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                User Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">User ID</Typography>
                  <TextField source="userId" />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">User Email</Typography>
                  <FunctionField
                    render={(record: any) => record.userEmail || '-'}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Suggestion Content */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Suggestion Content
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">Title</Typography>
                <TextField source="title" sx={{ display: 'block' }} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">Suggestion</Typography>
                <TextField source="suggestion" sx={{ display: 'block' }} />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Reasoning</Typography>
                <FunctionField
                  render={(record: any) => record.reasoning || 'No reasoning provided'}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Metadata
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Category</Typography>
                  <FunctionField
                    render={(record: any) => record.category || '-'}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Language</Typography>
                  <TextField source="language" />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Created</Typography>
                  <DateField source="createdAt" showTime />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Actions */}
          <SuggestionActions />
        </Box>
      </SimpleShowLayout>
    </Show>
  );
}
